import { supabase } from "../supabaseClient";

const ADMIN_UIDS = (process.env.REACT_APP_ADMIN_UIDS || process.env.REACT_APP_ADMIN_UID || "")
  .split(",")
  .map((value) => value.trim())
  .filter(Boolean);
const rawApiBaseUrl =
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_BACKEND_URL ||
  "";

const apiBaseUrl = (() => {
  const normalizedBaseUrl = rawApiBaseUrl.trim().replace(/\/+$/, "");

  if (normalizedBaseUrl) {
    return normalizedBaseUrl;
  }

  if (typeof window !== "undefined") {
    return window.location.origin.replace(/\/+$/, "");
  }

  return "";
})();

const getAccessToken = async () => {
  const {
    data: { session },
    error,
  } = await supabase.auth.getSession();

  if (error || !session?.access_token) {
    throw new Error(error?.message || "Unable to verify signed-in session.");
  }

  return session.access_token;
};

const parseJsonResponse = async (response) => {
  const contentType = response.headers.get("content-type") || "";

  if (!contentType.includes("application/json")) {
    const rawText = await response.text().catch(() => "");
    return { payload: null, rawText };
  }

  return { payload: await response.json().catch(() => null), rawText: "" };
};

export const isAdminUser = async (user) => {
  if (!user?.id) {
    return false;
  }

  if (ADMIN_UIDS.includes(user.id)) {
    return true;
  }

  const { data, error } = await supabase
    .from("admin_users")
    .select("id, user_id")
    .eq("user_id", user.id)
    .limit(1);

  if (error) {
    if (["42P01", "PGRST205"].includes(error.code)) {
      return false;
    }

    throw error;
  }

  return Boolean(data?.[0]);
};

export const loadAdminDashboardData = async () => {
  const [ordersResult, orderItemsResult, contactsResult, productsResult] = await Promise.all([
    supabase.from("orders").select("*").order("created_at", { ascending: false }),
    supabase.from("order_items").select("id, product_id, product_name"),
    supabase.from("contact_messages").select("*").order("created_at", { ascending: false }),
    supabase.from("products").select("id", { count: "exact", head: true }),
  ]);

  if (ordersResult.error) {
    throw ordersResult.error;
  }

  if (orderItemsResult.error) {
    throw orderItemsResult.error;
  }

  if (contactsResult.error) {
    throw contactsResult.error;
  }

  const orderItems = orderItemsResult.data || [];
  const uniqueOrderedProducts = new Set(
    orderItems
      .map((item) => item.product_id || item.product_name)
      .filter(Boolean)
  );
  const productsTableMissing = ["42P01", "PGRST205"].includes(productsResult.error?.code);

  if (productsResult.error && !productsTableMissing) {
    throw productsResult.error;
  }

  return {
    orders: ordersResult.data || [],
    orderItems,
    contacts: contactsResult.data || [],
    totalProducts: productsTableMissing ? uniqueOrderedProducts.size : productsResult.count || 0,
  };
};

export const updateOrderStatus = async (orderId, status) => {
  const normalizedStatus = status?.trim();

  if (!orderId || !normalizedStatus) {
    throw new Error("Missing order id or status.");
  }

  const accessToken = await getAccessToken();
  const endpoint = `${apiBaseUrl}/api/admin/orders/${encodeURIComponent(orderId)}/status`;
  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      status: normalizedStatus,
    }),
  });

  const { payload, rawText } = await parseJsonResponse(response);

  if (!response.ok || payload?.status !== "success" || !payload?.order) {
    throw new Error(
      payload?.message ||
        rawText ||
        "Unable to update order status on the server."
    );
  }

  return payload.order;
};

const attachOrderItems = (orders = [], orderItems = []) => {
  const itemsByOrderId = orderItems.reduce((map, item) => {
    const currentItems = map.get(item.order_id) || [];
    currentItems.push(item);
    map.set(item.order_id, currentItems);
    return map;
  }, new Map());

  return orders.map((order) => ({
    ...order,
    order_items: itemsByOrderId.get(order.id) || [],
  }));
};

const attachStatusHistory = (orders = [], history = []) => {
  const byOrderId = history.reduce((map, entry) => {
    const entries = map.get(entry.order_id) || [];
    entries.push(entry);
    map.set(entry.order_id, entries);
    return map;
  }, new Map());
  return orders.map((order) => ({ ...order, status_history: byOrderId.get(order.id) || [] }));
};

export const loadAdminOrders = async () => {
  const ordersResult = await supabase
    .from("orders")
    .select("*")
    .order("created_at", { ascending: false });

  if (ordersResult.error) {
    throw ordersResult.error;
  }

  const orders = ordersResult.data || [];
  const orderIds = orders.map((order) => order.id).filter(Boolean);

  if (!orderIds.length) {
    return [];
  }

  const orderItemsResult = await supabase
    .from("order_items")
    .select("*")
    .in("order_id", orderIds);

  if (orderItemsResult.error) {
    throw orderItemsResult.error;
  }

  const historyResult = await supabase
    .from("order_status_history")
    .select("id, order_id, status, created_at")
    .in("order_id", orderIds)
    .order("created_at", { ascending: true });
  if (historyResult.error) throw historyResult.error;

  return attachStatusHistory(attachOrderItems(orders, orderItemsResult.data || []), historyResult.data || []);
};

export const updateAdminOrderStatus = async (orderId, status) => {
  return updateOrderStatus(orderId, status);
};

export const cancelAdminOrder = async (orderId) => updateAdminOrderStatus(orderId, "cancelled");

export const bookAdminShipment = async (orderId) => {
  if (!orderId) {
    throw new Error("Missing order id.");
  }

  const accessToken = await getAccessToken();
  const endpoint = `${apiBaseUrl}/api/admin/orders/${encodeURIComponent(orderId)}/book-shipment`;
  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  });

  const { payload, rawText } = await parseJsonResponse(response);

  if (!response.ok || payload?.status !== "success" || !payload?.order) {
    throw new Error(
      payload?.message ||
        rawText ||
        "Unable to book shipment on the server."
    );
  }

  return payload.order;
};
